/**
 * Columns-step type override → canonical-kind reverse map (S5 real-DB fix).
 *
 * BUG THIS CLOSES: the Columns editor's "Type" select (`ColumnEditorPopover`)
 * lets a user override a column's rendered native type (e.g. text-inferred
 * `qty` → `INTEGER`), but `applyOverrides` only ever wrote `nativeType` +
 * `nativeTypeIsCustom` — `column.canonical` (the type EVERY value-facing
 * consumer reads: `prevalidateRow`'s `conversionCandidateFor`, and every
 * `coerceForXxx` in `electron/data-import/sessions/cellCoercion.ts`) never
 * changed. So overriding a column's type only relabelled the DDL — invalid
 * values for the NEW type were never rejected/skipped, and on an engine with
 * loose column typing (SQLite) or non-strict numeric coercion (MySQL) they
 * were silently written instead of failing. Found via the S5 SQLite real-DB
 * "skip-rows with a real failure" test (`Docs/DataImport-Plan.md` L761-762),
 * which requires exactly this override → rejection path to work. CROSS-ENGINE
 * IMPACT: this table is consulted by `applyOverrides` for every writable
 * engine, so all four remaining S5 engine specs inherit the fix.
 *
 * Only EXACT matches against the curated preset list (`getTypePresets` /
 * `electron/data-import/engineTypePresets.ts`) resolve — a free-text custom
 * native type stays unmapped (canonical untouched, today's behavior) since
 * reliably reverse-parsing arbitrary per-engine DDL tokens is a much larger,
 * unbounded problem already solved (forward direction only) by
 * `electron/cross-engine/engines/*\/toCanonical.ts`. Values below are manually
 * traced against those exact functions (not re-implemented/guessed) so the
 * kind an override resolves to always matches what real schema introspection
 * of that same declared type would produce. Runtime code from `electron/` is
 * never imported directly into the renderer (see the re-export comment on
 * `src/types/electron.d.ts`) — this is a manually-kept-in-sync mirror, the
 * same pattern `candidateKeyForColumn.ts` uses for `conversionCandidateFor`.
 *
 * Keep in sync: a change to a preset string in `ENGINE_TYPE_PRESETS` or to a
 * `toCanonical.ts` mapping rule that covers one of these tokens should update
 * the matching entry here too.
 */
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { ImportColumnPlan } from '../../../../types/electron';

type Canonical = ImportColumnPlan['canonical'];
type PresetMap = Record<string, Canonical>;

const MSSQL_PRESETS: PresetMap = {
  int: { kind: 'int32', raw: 'int' },
  bigint: { kind: 'int64', raw: 'bigint' },
  bit: { kind: 'boolean', raw: 'bit' },
  'decimal(18,2)': { kind: 'decimal', precision: 18, scale: 2, raw: 'decimal' },
  float: { kind: 'float64', raw: 'float' },
  'nvarchar(255)': { kind: 'varchar', length: 255, unicode: true, raw: 'nvarchar' },
  'nvarchar(max)': { kind: 'text', unicode: true, raw: 'nvarchar' },
  datetime2: { kind: 'timestamp', precision: 7, raw: 'datetime2' },
  date: { kind: 'date', raw: 'date' },
  time: { kind: 'time', precision: 7, raw: 'time' },
  uniqueidentifier: { kind: 'uuid', raw: 'uniqueidentifier' },
  'varbinary(max)': { kind: 'blob', raw: 'varbinary' },
};

const PGSQL_PRESETS: PresetMap = {
  integer: { kind: 'int32', raw: 'integer' },
  bigint: { kind: 'int64', raw: 'bigint' },
  boolean: { kind: 'boolean', raw: 'boolean' },
  'numeric(18,2)': { kind: 'decimal', precision: 18, scale: 2, raw: 'numeric' },
  'double precision': { kind: 'float64', raw: 'double precision' },
  text: { kind: 'text', unicode: true, raw: 'text' },
  'varchar(255)': { kind: 'varchar', length: 255, unicode: true, raw: 'varchar' },
  timestamptz: { kind: 'timestamptz', raw: 'timestamptz' },
  date: { kind: 'date', raw: 'date' },
  time: { kind: 'time', raw: 'time' },
  uuid: { kind: 'uuid', raw: 'uuid' },
  jsonb: { kind: 'json', raw: 'jsonb' },
  bytea: { kind: 'blob', raw: 'bytea' },
};

const MYSQL_PRESETS: PresetMap = {
  INT: { kind: 'int32', raw: 'int' },
  BIGINT: { kind: 'int64', raw: 'bigint' },
  'TINYINT(1)': { kind: 'boolean', raw: 'tinyint' },
  'DECIMAL(18,2)': { kind: 'decimal', precision: 18, scale: 2, raw: 'decimal' },
  DOUBLE: { kind: 'float64', raw: 'double' },
  'VARCHAR(255)': { kind: 'varchar', length: 255, raw: 'varchar' },
  TEXT: { kind: 'text', raw: 'text' },
  DATETIME: { kind: 'timestamp', raw: 'datetime' },
  DATE: { kind: 'date', raw: 'date' },
  TIME: { kind: 'time', raw: 'time' },
  // MySQL's toCanonical has no uuid special-case — CHAR(36) genuinely resolves
  // to plain `char`, even though it's the idiomatic MySQL UUID-as-text shape.
  'CHAR(36)': { kind: 'char', length: 36, raw: 'char' },
  BLOB: { kind: 'blob', raw: 'blob' },
};

const ORACLE_PRESETS: PresetMap = {
  // Oracle's NUMBER(p) → int-width heuristic is precision-banded (p<=4→int16,
  // p<=9→int32, else int64) — both NUMBER(10) and NUMBER(19) land on int64.
  'NUMBER(10)': { kind: 'int64', raw: 'NUMBER' },
  'NUMBER(19)': { kind: 'int64', raw: 'NUMBER' },
  'NUMBER(18,2)': { kind: 'decimal', precision: 18, scale: 2, raw: 'NUMBER' },
  BINARY_DOUBLE: { kind: 'float64', raw: 'BINARY_DOUBLE' },
  'VARCHAR2(255)': { kind: 'varchar', length: 255, raw: 'VARCHAR2' },
  CLOB: { kind: 'text', raw: 'CLOB' },
  // Oracle DATE carries a time-of-day → canonical `timestamp`, not `date`.
  DATE: { kind: 'timestamp', raw: 'DATE' },
  TIMESTAMP: { kind: 'timestamp', raw: 'TIMESTAMP' },
  'TIMESTAMP WITH TIME ZONE': { kind: 'timestamptz', raw: 'TIMESTAMP WITH TIME ZONE' },
  // RAW is a general bounded-byte type in Oracle's toCanonical — no uuid special-case.
  'RAW(16)': { kind: 'varbinary', length: 16, raw: 'RAW' },
  BLOB: { kind: 'blob', raw: 'BLOB' },
};

const SQLITE_PRESETS: PresetMap = {
  INTEGER: { kind: 'int32', raw: 'integer' },
  REAL: { kind: 'float64', raw: 'real' },
  TEXT: { kind: 'text', raw: 'text' },
  NUMERIC: { kind: 'decimal', raw: 'numeric' },
  BLOB: { kind: 'blob', raw: 'blob' },
  'VARCHAR(255)': { kind: 'varchar', length: 255, raw: 'varchar' },
  BOOLEAN: { kind: 'boolean', raw: 'boolean' },
  DATETIME: { kind: 'timestamp', raw: 'datetime' },
};

const PRESET_CANONICAL_BY_ENGINE: Record<'mssql' | 'pgsql' | 'mysql' | 'oracle' | 'sqlite', PresetMap> = {
  mssql: MSSQL_PRESETS,
  pgsql: PGSQL_PRESETS,
  mysql: MYSQL_PRESETS,
  oracle: ORACLE_PRESETS,
  sqlite: SQLITE_PRESETS,
};

/** `mock` mimics MSSQL-style metadata (mirrors `getTypePresets`'s own aliasing); `kusto` and `azurestorage` are not SQL import targets. */
function presetTableFor(engine: DatabaseEngineId | undefined): PresetMap | undefined {
  if (engine === 'mock') return PRESET_CANONICAL_BY_ENGINE.mssql;
  if (engine === undefined || engine === 'kusto' || engine === 'azurestorage') return undefined;
  return PRESET_CANONICAL_BY_ENGINE[engine];
}

/**
 * Resolves a Columns-step native-type override back to the `CanonicalType`
 * schema introspection would produce for that same declared type — ONLY for
 * an exact curated-preset match. Returns `undefined` for a free-text custom
 * override, an unresolved engine, or any string outside the preset list —
 * callers must leave `canonical` untouched in that case (today's behavior).
 */
export function presetNativeTypeToCanonical(
  engine: DatabaseEngineId | undefined,
  nativeType: string,
): Canonical | undefined {
  return presetTableFor(engine)?.[nativeType];
}
